import { concat, fn } from "@ember/helper";
import { on } from "@ember/modifier";
import didInsert from "@ember/render-modifiers/modifiers/did-insert";
import willDestroy from "@ember/render-modifiers/modifiers/will-destroy";
import { LinkTo } from "@ember/routing";
import { trustHTML } from "@ember/template";
import bodyClass from "discourse/helpers/body-class";
import getURL from "discourse/lib/get-url";
import { eq } from "discourse/truth-helpers";
import DButton from "discourse/ui-kit/d-button";
import DDecoratedHtml from "discourse/ui-kit/d-decorated-html";
import DUserLink from "discourse/ui-kit/d-user-link";
import dAvatar from "discourse/ui-kit/helpers/d-avatar";
import dConcatClass from "discourse/ui-kit/helpers/d-concat-class";
import dFormatDate from "discourse/ui-kit/helpers/d-format-date";
import dIcon from "discourse/ui-kit/helpers/d-icon";
import { i18n } from "discourse-i18n";
import ModMarketGallery from "discourse/plugins/discourse-mod-market/discourse/components/mod-market-gallery";

export default <template>
  {{bodyClass "mod-market-page"}}
  <main
    class={{dConcatClass
      "mod-market-detail"
      (if @controller.detailsExpanded "is-expanded")
    }}
  >
    <nav
      class="mod-market-detail__breadcrumb"
      aria-label={{i18n "discourse_mod_market.detail.breadcrumb"}}
    >
      <a
        class="mod-market-detail__back"
        href={{getURL "/mods"}}
        {{on "click" @controller.goBack}}
      >
        <img
          class="mod-market-detail__back-icon"
          src={{getURL
            "/plugins/discourse-mod-market/images/mod-detail-back.svg"
          }}
          alt=""
        />
        <span>{{i18n "discourse_mod_market.detail.back"}}</span>
      </a>
      <span class="mod-market-detail__breadcrumb-trail">
        <LinkTo @route="mod-market" class="mod-market-detail__market-link">
          {{i18n "discourse_mod_market.catalog_title"}}
        </LinkTo>
        <span class="mod-market-detail__breadcrumb-icon" aria-hidden="true">
          <img
            src={{getURL
              "/plugins/discourse-mod-market/images/mod-detail-breadcrumb-arrow.svg"
            }}
            alt=""
          />
        </span>
        <span class="mod-market-detail__breadcrumb-title">{{@model.title}}</span>
      </span>
    </nav>

    <div class="mod-market-detail__surface">
      <ModMarketGallery @items={{@model.gallery}} />

      <section class="mod-market-detail__summary">
        <header class="mod-market-detail__header">
          <div class="mod-market-detail__heading">
            <p class="mod-market-detail__game">{{@model.type_name}}</p>
            <h1>{{@model.title}}</h1>
            {{#if @model.author}}
              <div class="mod-market-detail__author">
                <DUserLink
                  @user={{@model.author}}
                  class="mod-market-detail__author-link"
                >
                  {{dAvatar @model.author imageSize="tiny" loading="lazy"}}
                  <span>
                    {{#if (eq @model.author.name @model.author.username)}}
                      {{@model.author.username}}
                    {{else if @model.author.name}}
                      {{i18n
                        "discourse_mod_market.user.username_with_nickname"
                        username=@model.author.username
                        nickname=@model.author.name
                      }}
                    {{else}}
                      {{@model.author.username}}
                    {{/if}}
                  </span>
                </DUserLink>
                {{#if @model.updated_at}}
                  <span aria-hidden="true">·</span>
                  <span>
                    {{dFormatDate @model.updated_at format="tiny"}}
                    {{i18n "discourse_mod_market.card.updated"}}
                  </span>
                {{/if}}
              </div>
            {{/if}}
          </div>

          <DButton
            @action={{@controller.toggleFavorite}}
            @disabled={{@controller.favoriteDisabled}}
            @isLoading={{@controller.favoriteSaving}}
            @translatedAriaLabel={{i18n
              (if
                @controller.favorited
                "discourse_mod_market.actions.unfavorite_with_count"
                "discourse_mod_market.actions.favorite_with_count"
              )
              count=@controller.favoriteCount
            }}
            class={{dConcatClass
              "mod-market-detail__favorite"
              (if @controller.favorited "is-selected")
            }}
          >
            <img
              class="mod-market-detail__favorite-icon"
              src={{getURL
                "/plugins/discourse-mod-market/images/mod-detail-favorite.svg"
              }}
              alt=""
            />
            <span>{{@controller.favoriteCount}}</span>
          </DButton>
          {{#if @controller.favoriteDisabled}}
            <p class="mod-market-detail__action-note">
              {{i18n @controller.favoriteUnavailableMessageKey}}
            </p>
          {{/if}}
        </header>

        <section
          class="mod-market-detail__versions"
          aria-labelledby="mod-market-detail-versions-title"
        >
          <h2 id="mod-market-detail-versions-title">
            {{i18n "discourse_mod_market.detail.select_version"}}
          </h2>
          <div class="mod-market-detail__version-control">
            <div class="mod-market-detail__version-list">
              {{#each @controller.availableVersions as |version|}}
                <button
                  type="button"
                  data-mod-market-version={{version.version}}
                  class={{dConcatClass
                    "mod-market-detail__version-option"
                    (if
                      (eq @controller.selectedVersion.version version.version)
                      "is-selected"
                    )
                  }}
                  aria-pressed={{if
                    (eq @controller.selectedVersion.version version.version)
                    "true"
                    "false"
                  }}
                  {{on "click" (fn @controller.selectVersion version.version)}}
                >
                  <strong>{{version.version}}</strong>
                  <span>
                    {{i18n
                      (if
                        version.is_latest
                        "discourse_mod_market.detail.latest_version"
                        "discourse_mod_market.detail.historical_version"
                      )
                    }}
                  </span>
                  {{#if version.is_latest}}
                    <span class="mod-market-detail__recommended">
                      {{i18n "discourse_mod_market.detail.recommended"}}
                    </span>
                  {{/if}}
                </button>
              {{/each}}
            </div>
            <button
              type="button"
              class="mod-market-detail__version-next"
              aria-label={{i18n "discourse_mod_market.detail.more_versions"}}
              {{on "click" @controller.scrollVersions}}
            >
              {{dIcon "chevron-right"}}
            </button>
          </div>
        </section>

        <section class="mod-market-detail__content">
          <div class="mod-market-detail__tab-navigation">
            <div
              class="mod-market-detail__tabs"
              role="tablist"
              aria-label={{i18n "discourse_mod_market.detail.tabs_label"}}
            >
              {{#each @controller.availableTabs as |tab|}}
                <button
                  type="button"
                  role="tab"
                  id={{concat "mod-market-detail-tab-" tab}}
                  data-mod-market-tab={{tab}}
                  class={{dConcatClass
                    "mod-market-detail__tab"
                    (if (eq @controller.activeTab tab) "is-selected")
                  }}
                  aria-selected={{if
                    (eq @controller.activeTab tab)
                    "true"
                    "false"
                  }}
                  aria-controls="mod-market-detail-panel"
                  tabindex={{if (eq @controller.activeTab tab) "0" "-1"}}
                  {{on "click" (fn @controller.selectTab tab)}}
                  {{on "keydown" (fn @controller.navigateTabs tab)}}
                >
                  {{i18n (concat "discourse_mod_market.detail.tabs." tab)}}
                </button>
              {{/each}}
            </div>

            <button
              type="button"
              class="mod-market-detail__expand-toggle"
              aria-expanded={{if @controller.detailsExpanded "true" "false"}}
              aria-controls="mod-market-detail-panel"
              aria-label={{i18n
                (if
                  @controller.detailsExpanded
                  "discourse_mod_market.detail.collapse"
                  "discourse_mod_market.detail.expand"
                )
              }}
              {{on "click" @controller.toggleDetailsExpanded}}
            >
              <img
                src={{getURL
                  (if
                    @controller.detailsExpanded
                    "/plugins/discourse-mod-market/images/mod-detail-collapse.svg"
                    "/plugins/discourse-mod-market/images/mod-detail-expand.svg"
                  )
                }}
                alt=""
              />
              <span>
                {{i18n
                  (if
                    @controller.detailsExpanded
                    "discourse_mod_market.detail.collapse"
                    "discourse_mod_market.detail.expand"
                  )
                }}
              </span>
            </button>
          </div>

          <div
            id="mod-market-detail-panel"
            class={{dConcatClass
              "mod-market-detail__panel"
              (if (eq @controller.activeTab "comments") "is-comments")
            }}
            role="tabpanel"
            aria-labelledby={{concat
              "mod-market-detail-tab-"
              @controller.activeTab
            }}
          >
            {{#if (eq @controller.activeTab "comments")}}
              {{#if @controller.discussionUrl}}
                <iframe
                  class="mod-market-detail__discussion"
                  src={{@controller.discussionUrl}}
                  title={{i18n
                    "discourse_mod_market.discussion.title"
                    modTitle=@model.title
                  }}
                  {{didInsert @controller.registerDiscussionFrame}}
                  {{on "load" @controller.discussionFrameLoaded}}
                  {{willDestroy @controller.unregisterDiscussionFrame}}
                ></iframe>
              {{else}}
                <div class="mod-market-detail__discussion-status">
                  {{dIcon "triangle-exclamation"}}
                  <h2>{{i18n
                      (if
                        (eq @model.discussion_status "initializing")
                        "discourse_mod_market.discussion.initializing"
                        "discourse_mod_market.discussion.unavailable"
                      )
                    }}</h2>
                  <p>{{i18n "discourse_mod_market.discussion.description"}}</p>
                </div>
              {{/if}}
            {{else if (eq @controller.activeTab "changelog")}}
              <ol class="mod-market-detail__changelog">
                {{#each @controller.availableVersions as |version|}}
                  <li
                    class={{dConcatClass
                      "mod-market-detail__changelog-item"
                      (if
                        (eq
                          @controller.selectedVersion.version version.version
                        )
                        "is-selected"
                      )
                    }}
                    aria-current={{if
                      (eq @controller.selectedVersion.version version.version)
                      "true"
                    }}
                  >
                    <header class="mod-market-detail__changelog-header">
                      <strong>{{version.version}}</strong>
                      {{#if version.published_at}}
                        <span>
                          {{dFormatDate version.published_at format="tiny"}}
                          {{i18n "discourse_mod_market.card.updated"}}
                        </span>
                      {{/if}}
                    </header>
                    {{#if version.changelog_cooked}}
                      <DDecoratedHtml
                        @html={{trustHTML version.changelog_cooked}}
                        @className="cooked mod-market-detail__changelog-content"
                      />
                    {{else}}
                      <p class="mod-market-detail__changelog-empty">
                        {{i18n
                          "discourse_mod_market.detail.empty.changelog"
                        }}
                      </p>
                    {{/if}}
                  </li>
                {{/each}}
              </ol>
            {{else}}
              {{#if @controller.selectedDescriptionCooked}}
                <DDecoratedHtml
                  @html={{trustHTML @controller.selectedDescriptionCooked}}
                  @className="cooked mod-market-detail__cooked"
                />
              {{else}}
                <div class="mod-market-detail__empty-content">
                  {{dIcon "file-lines"}}
                  <p>{{i18n
                      "discourse_mod_market.detail.empty.description"
                    }}</p>
                </div>
              {{/if}}

              {{#if @controller.selectedCompatibility}}
                <div class="mod-market-detail__compatibility">
                  <div>
                    <strong>{{i18n
                        "discourse_mod_market.detail.compatibility"
                      }}</strong>
                    <span>{{@controller.selectedCompatibility}}</span>
                  </div>
                </div>
              {{/if}}
            {{/if}}
          </div>
        </section>

        {{#if @controller.showCommentEntry}}
          <button
            type="button"
            class="mod-market-detail__comment-entry"
            {{on "click" @controller.openCommentComposer}}
          >
            {{#if @controller.currentUser}}
              {{dAvatar @controller.currentUser imageSize="small" loading="lazy"}}
            {{else}}
              {{dIcon "user"}}
            {{/if}}
            <span>{{i18n "discourse_mod_market.detail.comment_prompt"}}</span>
          </button>
        {{/if}}
      </section>
    </div>
  </main>
</template>
